import React, { useEffect, useRef } from "react";
import { FaCheck, FaEdit, FaPlus, FaSave, FaTimes, FaTrash } from "react-icons/fa";
import { GiBlackBelt } from "react-icons/gi";

import {
  DEFAULT_BELT_PHOTO,
  formatGradeDate,
  getAdherentName,
  getBeltImage,
  getBeltImageFromBelt,
  getBeltName,
  getGroupNames,
} from "../utils/passageGradeCoachUtils";

function CoachGradeHistoryModal({
  isOpen,
  row,
  belts = [],
  form,
  selectedGrade,
  saving,
  deletingGradeId,
  onClose,
  onChange,
  onSubmit,
  onEdit,
  onCancelEdit,
  onDelete,
}) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const selectedBelt = belts.find(
    (belt) => String(belt.id) === String(form.belt_id),
  );

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/70 px-3 backdrop-blur-sm sm:items-center sm:px-4">
      <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[2rem] border border-white/20 bg-white shadow-2xl dark:bg-slate-900 sm:rounded-[2rem]">
        <div className="border-b border-slate-200 bg-gradient-to-br from-sky-700 via-blue-700 to-indigo-800 p-5 text-white dark:border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/70">
                Historique des ceintures
              </p>

              <h2 className="mt-2 font-titre text-2xl font-bold">
                {getAdherentName(row?.adherent)}
              </h2>

              <p className="mt-2 text-sm font-medium text-white/75">
                {getGroupNames(row)}
              </p>
            </div>

            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25 focus:outline-none focus:ring-4 focus:ring-white/30"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid gap-5 lg:grid-cols-[1.35fr_0.65fr]">
            <section className="rounded-[2rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-200">
                    Historique
                  </p>

                  <h3 className="mt-1 font-titre text-xl font-bold text-slate-950 dark:text-white">
                    {row?.grades?.length || 0} passage
                    {(row?.grades?.length || 0) > 1 ? "s" : ""}
                  </h3>
                </div>
              </div>

              {!row?.grades?.length ? (
                <div className="mt-5 rounded-[1.5rem] border border-dashed border-slate-300 bg-white p-8 text-center dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-white/50">
                    <GiBlackBelt className="text-2xl" />
                  </div>

                  <p className="mt-4 text-sm font-bold text-slate-700 dark:text-white">
                    Aucun passage enregistré.
                  </p>

                  <p className="mt-1 text-xs font-medium text-slate-500 dark:text-white/45">
                    Ajoute une première ceinture avec le formulaire.
                  </p>
                </div>
              ) : (
                <div className="mt-5 overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[620px] text-left">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:border-white/10 dark:bg-white/[0.03]">
                          <th className="px-4 py-3">Ceinture</th>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100 dark:divide-white/10">
                        {row.grades.map((grade, index) => {
                          const isCurrent = index === 0;
                          const isDeleting = deletingGradeId === grade.id;

                          return (
                            <tr key={grade.id}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="flex h-12 w-24 items-center justify-center rounded-2xl bg-slate-50 dark:bg-white/[0.04]">
                                    <img
                                      src={getBeltImage(grade)}
                                      alt={getBeltName(grade)}
                                      className="h-9 w-full object-contain"
                                      onError={(event) => {
                                        event.currentTarget.src =
                                          DEFAULT_BELT_PHOTO;
                                      }}
                                    />
                                  </div>

                                  <div>
                                    <p className="font-bold text-slate-950 dark:text-white">
                                      {getBeltName(grade)}
                                    </p>

                                    {isCurrent && (
                                      <p className="mt-1 text-xs font-bold text-sky-700 dark:text-sky-200">
                                        Ceinture actuelle
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-600 dark:text-white/60">
                                {formatGradeDate(grade.date_passage)}
                              </td>

                              <td className="px-4 py-3">
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => onEdit(grade)}
                                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-3 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5"
                                  >
                                    <FaEdit />
                                    Modifier
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => onDelete(grade)}
                                    disabled={isDeleting}
                                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
                                  >
                                    <FaTrash />
                                    {isDeleting ? "..." : "Suppr."}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>

            <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/40 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-200">
                {selectedGrade ? "Modification" : "Nouveau passage"}
              </p>

              <h3 className="mt-2 font-titre text-2xl font-bold text-slate-950 dark:text-white">
                {selectedGrade
                  ? "Modifier le passage"
                  : "Ajouter une ceinture"}
              </h3>

              {selectedBelt && (
                <div className="mt-4 flex h-20 items-center justify-center rounded-[1.5rem] bg-slate-50 dark:bg-slate-950">
                  <img
                    src={getBeltImageFromBelt(selectedBelt)}
                    alt={selectedBelt.nom}
                    className="h-16 w-full object-contain"
                  />
                </div>
              )}

              <form onSubmit={onSubmit} className="mt-5 space-y-4">
                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Ceinture
                  </span>

                  <select
                    name="belt_id"
                    value={form.belt_id}
                    onChange={onChange}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
                  >
                    <option value="">Sélectionnez une ceinture</option>

                    {belts.map((belt) => (
                      <option key={belt.id} value={belt.id}>
                        {belt.nom}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Date de passage
                  </span>

                  <input
                    type="date"
                    name="date_passage"
                    value={form.date_passage}
                    onChange={onChange}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
                  />
                </label>

                <div className="grid gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-blue-700 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
                  >
                    {saving ? (
                      <FaSave className="animate-pulse" />
                    ) : selectedGrade ? (
                      <FaCheck />
                    ) : (
                      <FaPlus />
                    )}

                    {saving
                      ? "Enregistrement..."
                      : selectedGrade
                        ? "Modifier"
                        : "Ajouter"}
                  </button>

                  {selectedGrade && (
                    <button
                      type="button"
                      onClick={onCancelEdit}
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
                    >
                      <FaTimes />
                      Annuler la modification
                    </button>
                  )}
                </div>
              </form>
            </section>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
          >
            <FaTimes />
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default CoachGradeHistoryModal;